/**
 * Range Advantage on Board — pick BTN raiser or BB caller.
 *
 * Sheet 3's cheat-sheet table maps flop archetypes to range-advantage
 * winner. Encoded as a curated board list so each question has an
 * unambiguous answer:
 *   - A-high, K-high, Q-high, J-high                → BTN
 *   - Middling unconnected (T-7-3, 9-6-2)           → BTN (smaller edge)
 *   - Paired HIGH boards (A-A-4, K-K-7)             → BTN
 *   - Connected middling (8-7-6, 7-6-5)             → BB
 *   - Low boards (5-4-3, 6-5-2)                     → BB
 *   - Paired LOW boards (5-5-2, 4-4-3)              → BB
 *
 * Q-J-T type boards are intentionally excluded — sheet 3 calls them
 * "roughly even, slight nut edge to BB." Ambiguous for this quiz;
 * that nuance belongs in the Range-vs-Nut quiz.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type Side = "BTN" | "BB";

export type BoardCategory =
  | "a_high"
  | "k_high_dry"
  | "k_high_wet"
  | "q_high_dry"
  | "j_high_dry"
  | "middling_unconnected"
  | "paired_high"
  | "connected_middling"
  | "low"
  | "paired_low";

export interface RangeAdvBoard {
  board: [string, string, string];
  advantage: Side;
  category: BoardCategory;
}

/** Curated boards drawn from sheet 3 patterns. Pin via tests so the
 *  set + categories stay coherent if anyone edits. */
export const RANGE_ADVANTAGE_BANK: RangeAdvBoard[] = [
  // A-high → BTN (strong)
  { board: ["As", "7c", "2d"], advantage: "BTN", category: "a_high" },
  { board: ["Ah", "Td", "5c"], advantage: "BTN", category: "a_high" },
  { board: ["Ad", "Kh", "9s"], advantage: "BTN", category: "a_high" },
  { board: ["Ac", "8h", "3d"], advantage: "BTN", category: "a_high" },
  { board: ["As", "Jd", "4h"], advantage: "BTN", category: "a_high" },

  // K-high dry → BTN (strong)
  { board: ["Ks", "7c", "2d"], advantage: "BTN", category: "k_high_dry" },
  { board: ["Kd", "8h", "3c"], advantage: "BTN", category: "k_high_dry" },
  { board: ["Kc", "5s", "2h"], advantage: "BTN", category: "k_high_dry" },

  // K-high wet → BTN (smaller edge, still BTN)
  { board: ["Ks", "Jh", "Tc"], advantage: "BTN", category: "k_high_wet" },
  { board: ["Kh", "Td", "9s"], advantage: "BTN", category: "k_high_wet" },

  // Q-high dry → BTN
  { board: ["Qs", "7c", "2d"], advantage: "BTN", category: "q_high_dry" },
  { board: ["Qc", "5h", "3d"], advantage: "BTN", category: "q_high_dry" },

  // J-high dry → BTN small edge
  { board: ["Js", "6c", "2d"], advantage: "BTN", category: "j_high_dry" },
  { board: ["Jh", "4d", "2s"], advantage: "BTN", category: "j_high_dry" },

  // Middling unconnected → BTN small edge
  { board: ["Ts", "7c", "3d"], advantage: "BTN", category: "middling_unconnected" },
  { board: ["9h", "6d", "2c"], advantage: "BTN", category: "middling_unconnected" },
  { board: ["Tc", "6h", "2s"], advantage: "BTN", category: "middling_unconnected" },

  // Paired high → BTN
  { board: ["As", "Ac", "4d"], advantage: "BTN", category: "paired_high" },
  { board: ["Kh", "Ks", "7c"], advantage: "BTN", category: "paired_high" },
  { board: ["Qh", "Qs", "5c"], advantage: "BTN", category: "paired_high" },

  // Connected middling → BB
  { board: ["8c", "7h", "6d"], advantage: "BB", category: "connected_middling" },
  { board: ["7s", "6c", "5h"], advantage: "BB", category: "connected_middling" },
  { board: ["9s", "8c", "7h"], advantage: "BB", category: "connected_middling" },

  // Low → BB
  { board: ["5c", "4h", "3d"], advantage: "BB", category: "low" },
  { board: ["6s", "5c", "2d"], advantage: "BB", category: "low" },
  { board: ["6c", "4s", "3h"], advantage: "BB", category: "low" },

  // Paired low → BB (more trips in the caller's range)
  { board: ["5s", "5h", "2d"], advantage: "BB", category: "paired_low" },
  { board: ["4c", "4d", "3h"], advantage: "BB", category: "paired_low" },
  { board: ["6h", "6s", "2c"], advantage: "BB", category: "paired_low" },
];

const CATEGORY_PROSE: Record<BoardCategory, string> = {
  a_high: "A-high boards hit BTN's preflop range much harder: they raise more aces. BB rarely calls Ax preflop, so they have fewer top pairs and almost no overpairs.",
  k_high_dry: "K-high dry boards favor BTN strongly. BTN's range is loaded with KQ/KJ/KT and overpairs; BB defends some Kx but at a much lower frequency.",
  k_high_wet: "K-high wet boards still favor BTN but the edge shrinks: BB's small connectors and broadways start picking up draws and combo-strong holdings.",
  q_high_dry: "Q-high dry boards favor BTN: they raise QQ+/AQ/KQ and have overpairs; BB has fewer top-pair combos and minimal nut hands.",
  j_high_dry: "J-high dry boards favor BTN with a smaller edge: BTN still has more overpairs and JX top-pair, but BB defends enough JX to keep it close.",
  middling_unconnected: "Middling unconnected boards (T-7-3 type) favor BTN slightly: overpairs JJ+/TT dominate, and BB has fewer middle-pair value hands.",
  paired_high: "Paired high boards favor BTN heavily: BTN raises more of the paired card preflop. AA on A-A-x is the obvious example, but K-K-x and Q-Q-x play similarly.",
  connected_middling: "Connected middling boards (8-7-6) favor BB: small suited connectors live in BB's defending range. BTN has overpairs but very few straights or two-pair combos.",
  low: "Low boards (5-4-3, 6-5-2) favor BB: small pairs and connectors hit hard, and BTN's high-card-heavy range often whiffs entirely.",
  paired_low: "Paired LOW boards favor BB: BB defends more of the paired small card, so they have more trips. BTN has overpairs but few sets.",
};

export interface RangeAdvantageQuestion {
  board: [string, string, string];
  advantage: Side;
  category: BoardCategory;
  rationale: string;
}

/** Bank with rationale pre-attached — what the quiz route consumes. */
export const RANGE_ADVANTAGE_QUESTIONS: ReadonlyArray<RangeAdvantageQuestion> =
  RANGE_ADVANTAGE_BANK.map((b) => ({
    board: b.board,
    advantage: b.advantage,
    category: b.category,
    rationale: CATEGORY_PROSE[b.category],
  }));

export function generateQuestion(
  rand: () => number = Math.random,
): RangeAdvantageQuestion {
  return pickFrom(RANGE_ADVANTAGE_QUESTIONS, rand);
}

export const _internals = { RANGE_ADVANTAGE_BANK, CATEGORY_PROSE };
